import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, HostListener, QueryList, ViewChild, ViewChildren, AfterViewInit, Input } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, forkJoin, map, of, Subject, Subscription, switchMap, takeUntil, tap } from 'rxjs';
import { AuthService } from 'src/core/services/auth.service';
import { ChatService } from 'src/core/services/chat.service';
import { DataService } from 'src/core/services/data.service';
import { SearchService } from 'src/core/services/search.service';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { SelectedSiteService } from 'src/core/services/selectedsite.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { WidgetService } from 'src/core/services/widget.service';
import { ApiResponse, AutocompleteInterface, AutocompleteSearchResults, SelectedContent } from 'src/core/types/general.types';
import { ExactSite, SelectedSite } from 'src/core/types/selectedSite.types';
import { ExactSystem, SelectedSystem } from 'src/core/types/selectedSystem.types';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-autocomplete',
  imports: [CommonModule, TranslocoModule],
  templateUrl: './autocomplete.component.html',
  styleUrl: './autocomplete.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AutocompleteComponent implements AfterViewInit {

  constructor(
    private dataService: DataService,
    public selectedsystemService: SelectedsystemService,
    public chatService: ChatService,
    public searchService: SearchService,
    public widgetService: WidgetService,
    public authService: AuthService,
    private selectedContentService: SelectedContentService,
    private selectedSiteService: SelectedSiteService
  ) { }

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;
  private resizeObserver?: ResizeObserver;
  private cancelSearch$ = new Subject<void>();

  @ViewChild('search') search!: ElementRef;
  @ViewChild('popover') popover!: ElementRef;
  @ViewChildren('resultsdom') resultsdom!: QueryList<ElementRef>;

  @Input() disabled = false;
  businessPartnerMode = environment.businessPartnerMode;
  results: AutocompleteInterface = { deviceResults: [], siteResults: [] };
  searchWord: string = '';
  isLoading: boolean = false;
  isError = false;
  isEmpty = false;
  popoverwidth = 225;
  resultsVisible: boolean = false;
  activeIndex = -1;
  recentsearches: Array<any> = [];
  recentsearchessites: Array<any> = [];

  ngOnInit(): void {

    //initialize pipeline for retrieving results from cognitive search. Wait 400ms before executing the Cognitive Search commands
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap((searchTerm) => {
          this.results = { deviceResults: [], siteResults: [] };
          this.searchWord = searchTerm;
          this.isEmpty = false;
          this.isError = false;
          this.resultsVisible = this.searchWord.length > 0;
        }),
        filter((searchTerm) => searchTerm.length > 2),
        switchMap((searchTerm) => {
          const payload = {
            email_id: this.authService.userData?.basic["mail"],
            search_string: searchTerm
          };

          const payloadSites = {
            email_id: this.authService.userData?.basic["mail"],
            search_string: searchTerm,
            supported_only: true
          };

          this.isLoading = true;

          return forkJoin({
            device: this.dataService.searchDevice(payload),
            site: this.dataService.searchSite(payloadSites)
          }).pipe(
            takeUntil(this.cancelSearch$),
            finalize(() => {
              this.isLoading = false;
            }),
            map(({ device, site }): AutocompleteSearchResults => ({
              deviceResults: device,
              siteResults: site
            }))
          );
        }),
        catchError((err) => {
          console.log('Error:', err);
          this.isLoading = false;
          this.isError = true;
          this.clearSearch();
          return of(-1);
        }),
      )
      .subscribe({
        next: ({ deviceResults, siteResults }: AutocompleteSearchResults) => {

          const deviceResponse = deviceResults?.response || [];
          const deviceStatus = deviceResults?.status;
          const siteResponse = siteResults?.response || [];
          const siteStatus = siteResults?.status;

          this.results = { deviceResults: deviceResponse, siteResults: siteResponse };

          if (!deviceResults) {
            this.results = { deviceResults: [], siteResults: [] };
            this.isError = true;
            this.isLoading = false;
          }
          else {
            if (deviceResponse.length === 0 && siteResponse.length === 0) { this.isEmpty = true; }
            this.isError = false;
            this.results = { deviceResults: deviceResponse, siteResults: siteResponse };
            this.isLoading = false;
            if (this.searchWord.length > 0) { this.resultsVisible = true; }
          }
        },
        error: (err) => {
          this.isLoading = false;
          console.log(err);
        }
      });

    this.searchService.recents.subscribe((data) => {
      this.recentsearches = data;
    });

    this.searchService.recentsSites.subscribe((data) => {
      this.recentsearchessites = data;
    });

  }


  ngAfterViewInit(): void {
    const el: HTMLElement | undefined = this.search?.nativeElement as HTMLElement | undefined;
    if (!el) return;

    // Initial width check deferred to next tick to avoid NG0100
    setTimeout(() => {
      const initWidth = el.getBoundingClientRect().width || el.clientWidth;
      this.popoverwidth = Math.round(initWidth);
    });

    // Observe width changes
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const w = entry.contentRect?.width ?? (entry.target as HTMLElement).clientWidth;
          this.popoverwidth = Math.round(w);
        }
      });
      this.resizeObserver.observe(el);
    }
  }


  keepSearchOpen = (e) => {
    if (this.disabled) {
      this.resultsVisible = false;
      return;
    }

    this.resultsVisible = false;
  }

  onInput(event: Event): void {
    if (this.disabled) {
      this.clearSearch();
      return;
    }

    const raw = (event.target as HTMLInputElement).value;
    const searchQuery = raw ? raw.trim() : "";

    // If the query is now too short to search, abort any in-flight requests immediately
    // (otherwise the previous 3+ char request could still complete and show stale results).
    if (searchQuery.length <= 2) {
      this.cancelSearch$.next();
      this.isLoading = false;
      this.isError = false;
      this.isEmpty = false;
      this.activeIndex = -1;
      this.results = { deviceResults: [], siteResults: [] };
      //this.resultsVisible = false;
    }

    this.searchSubject.next(searchQuery);
  }

  ngOnDestroy(): void {
    // Abort any in-flight requests before teardown
    this.cancelSearch$.next();
    this.cancelSearch$.complete();
    this.searchSubject.complete();
    this.resizeObserver?.disconnect();
    this.searchSubscription?.unsubscribe();
  }

  clearSearch = () => {
    // Abort any in-flight search requests (Angular HttpClient will cancel on unsubscribe)
    this.cancelSearch$.next();

    // Clear the actual input so the next search starts from a new value
    const inputEl = this.search?.nativeElement as HTMLInputElement | undefined;
    if (inputEl) {
      inputEl.value = '';
    }

    // Emit '' so distinctUntilChanged won't block re-searching the same term (e.g. "roma")
    this.searchSubject.next('');

    this.results = { deviceResults: [], siteResults: [] };
    this.searchWord = "";
    this.isEmpty = false;
    this.isLoading = false;
    this.activeIndex = -1;
    this.resultsVisible = false;
  }

  closeOverlay = () => {
    this.resultsVisible = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const searchEl = this.search?.nativeElement as HTMLElement | undefined;
    const popoverEl = this.popover?.nativeElement as HTMLElement | undefined;
    if (!searchEl) return;
    const path = event.composedPath();
    const clickedInside = path.includes(searchEl) || (popoverEl && path.includes(popoverEl));
    if (!clickedInside && this.resultsVisible) {
      this.resultsVisible = false;
    }
  }

  setNewDevice(device: ExactSystem) {
    if (this.disabled) {
      return;
    }

    this.resultsVisible = false;
    this.search.nativeElement.blur();

    this.selectedsystemService.setNewDevice(device.MATERIAL_NUMBER, device.SERIAL_NUMBER).subscribe({
      next: (newdevice) => {
        if (newdevice !== null) {
          this.clearSearch();
          this.widgetService.resetWidgets();
          this.chatService.messages.next([]);
          this.updateRecentResults(device);
          return;
        }
      }
    });
  }

  setNewSite(siteId) {
    if (this.disabled) {
      return;
    }

    this.resultsVisible = false;
    this.search.nativeElement.blur();

    this.selectedSiteService.setNewSite(siteId).subscribe({
      next: (site) => {
        if (site !== null) {
          this.clearSearch();
          this.widgetService.resetWidgets();
          this.chatService.messages.next([]);
          this.updateRecentResultsSite(site);
          return;
        }
      }
    });
  }

  onKeyDown(event: KeyboardEvent) {
    if (this.disabled) return;

    if (!this.resultsVisible) return;
    if (this.searchWord.length <= 2) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const firstItem = this.resultsdom.first;
      this.activeIndex = 0;
      firstItem?.nativeElement?.focus();
    }

  }

  onKeyDownResults(event: KeyboardEvent) {
    if (this.disabled) return;

    if (!this.resultsVisible) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.activeIndex + 1 === this.resultsdom.length) return;
      const newItem = this.resultsdom.toArray()[this.activeIndex + 1];
      this.activeIndex++;
      newItem?.nativeElement?.focus();
    }

    if (event.key === 'ArrowUp') {

      event.preventDefault();
      if (this.activeIndex - 1 === -1) {
        this.search.nativeElement.focus();
        this.activeIndex = -1;
        setTimeout(() => {
          this.popover.nativeElement.scrollTop = 0;
        }, 50);
        return;
      }
      if (this.activeIndex - 1 === 0) {
        setTimeout(() => {
          this.popover.nativeElement.scrollTop = 0;
        }, 50);
      }
      const newItem = this.resultsdom.toArray()[this.activeIndex - 1];
      this.activeIndex--;
      newItem?.nativeElement?.focus();
    }
  }

  resetIndex() {
    if (this.disabled) {
      this.resultsVisible = false;
      this.activeIndex = -1;
      return;
    }

    this.resultsVisible = true;
    this.activeIndex = -1;
  }


  updateRecentResults(device: {}) {
    if (localStorage.getItem("recentsearches") === null) {
      localStorage.setItem("recentsearches", JSON.stringify([device]));
    }
    else {
      let currentArray: Array<any> = JSON.parse(localStorage.getItem("recentsearches"));

      //Check if device is already in the recent searches
      if (currentArray.some((item) => item['SERIAL_NUMBER'] + item['MATERIAL_NUMBER'] === device['SERIAL_NUMBER'] + item['MATERIAL_NUMBER'])) {
        //Remove the device from the array and add it to the front
        currentArray = currentArray.filter((item) => item['SERIAL_NUMBER'] + item['MATERIAL_NUMBER'] !== device['SERIAL_NUMBER'] + item['MATERIAL_NUMBER']);
        localStorage.setItem("recentsearches", JSON.stringify([device, ...currentArray]));
      }

      else if (currentArray.length === 5) {
        //Pop removes last element, unshift adds new element at top
        let poppedArray: Array<any> = currentArray.filter((item, index) => index !== 4);
        let newArray = [device, ...poppedArray];

        localStorage.setItem("recentsearches", JSON.stringify(newArray));
      }
      else {
        localStorage.setItem("recentsearches", JSON.stringify([device, ...currentArray]));
      }
    }

    //In any case, update the recent searches in the service
    this.searchService.recents.next(JSON.parse(localStorage.getItem("recentsearches")));
  }

  removeFromRecents(event: Event, device) {
    if (this.disabled) {
      return;
    }

    this.resultsVisible = true;

    event.stopPropagation();
    event.preventDefault();

    let currentArray: Array<any> = JSON.parse(localStorage.getItem("recentsearches"));
    let modifiedArray = currentArray.filter((item) => item["SERIAL_NUMBER"] + item["MATERIAL_NUMBER"] !== device["SERIAL_NUMBER"] + device["MATERIAL_NUMBER"]);
    localStorage.setItem("recentsearches", JSON.stringify(modifiedArray));

    //In any case, update the recent searches in the service
    this.searchService.recents.next(JSON.parse(localStorage.getItem("recentsearches")));

  }

  updateRecentResultsSite(site: SelectedSite) {
    if (localStorage.getItem("recentsearchessites") === null) {
      localStorage.setItem("recentsearchessites", JSON.stringify([site]));
    }
    else {
      let currentArray: Array<any> = JSON.parse(localStorage.getItem("recentsearchessites"));
      //Check if device is already in the recent searches
      if (currentArray.some((item) => item['Id'] === site['Id'])) {
        //Remove the device from the array and add it to the front
        currentArray = currentArray.filter((item) => item['Id'] !== site['Id']);
        localStorage.setItem("recentsearchessites", JSON.stringify([site, ...currentArray]));
      }

      else if (currentArray.length === 5) {
        //Pop removes last element, unshift adds new element at top
        let poppedArray: Array<any> = currentArray.filter((item, index) => index !== 4);
        let newArray = [site, ...poppedArray];

        localStorage.setItem("recentsearchessites", JSON.stringify(newArray));
      }
      else {
        localStorage.setItem("recentsearchessites", JSON.stringify([site, ...currentArray]));
      }
    }

    //In any case, update the recent searches in the service
    this.searchService.recentsSites.next(JSON.parse(localStorage.getItem("recentsearchessites")));
  }

  removeFromRecentsSites(event: Event, site) {
    if (this.disabled) {
      return;
    }

    this.resultsVisible = true;

    event.stopPropagation();
    event.preventDefault();

    let currentArray: Array<any> = JSON.parse(localStorage.getItem("recentsearchessites"));
    let modifiedArray = currentArray.filter((item) => item["Id"] !== site["Id"]);
    localStorage.setItem("recentsearchessites", JSON.stringify(modifiedArray));

    //In any case, update the recent searches in the service
    this.searchService.recentsSites.next(JSON.parse(localStorage.getItem("recentsearchessites")));
  }

  checkIfDupSerial(serialNumber) {
    return this.recentsearches.filter(item => item["SERIAL_NUMBER"] === serialNumber).length > 1;
  }

}
